// Keeps the viewer smooth and cheap:
//  * adaptive resolution: splat rendering is fill-rate bound (every pixel blends many translucent
//    splats), so the pixel ratio is lowered when frames drop and raised again when there is headroom;
//  * render on demand: once the camera is still, streaming has finished and no effect runs, no new
//    frames are drawn at all. Anything that changes the picture calls wake().
// With { scale: true } (the tour viewer) the canvas stays at the cap ratio and never resizes: the scene is drawn into
// the lower left part of a render target of canvas size (camera.rect), and a last pass stretches that part over the
// canvas. The part follows the GPU budget from frame to frame, shrinks only sideways while turning, and the last
// frame before the picture rests is drawn whole.
import {
    ADDRESS_CLAMP_TO_EDGE, ASPECT_AUTO, ASPECT_MANUAL, Color, Entity, FILTER_LINEAR, LAYERID_UI, PIXELFORMAT_RGBA8,
    RenderPassShaderQuad, RenderTarget, SEMANTIC_POSITION, ShaderUtils, Texture, Vec4
} from 'playcanvas';

const STILL_GRACE = 1.2;      // s to keep rendering after the last change (sorting settles)
const STEP = 0.25;
const STILL_FOR_RAISE = 0.4;  // s the camera must be still before a resolution increase is applied
// Render scale (scale mode). Thresholds from the 31.10.2026 measurements on an Apple M3 (docs/performance, C06),
// not tuned on phones yet
const GPU_TARGET = 13;        // ms GPU time per frame: a 60 Hz frame is 16.7 ms, the rest is slack for spikes and the page
const FILL_SHARE = 0.7;       // part of the GPU time that scales with pixels (hot view: 6.5 ms fixed + 20.8 ms fill)
const SCALE_FLOOR = 0.5;      // lowest scale per axis: half the cap ratio, today's lowest pixel ratio
const SCALE_SNAP = 32;        // scales snap to 1/32, so they do not change with every frame
const TURN = [30, 90];        // deg/s yaw speed: from the first x may drop with the speed, from the second down to the floor
const MISSED = 25;            // ms between two rendered frames: a missed vsync at 60 Hz
const STREAM_TAIL = 500;      // ms after the last chunk load before WebGL2 frame gaps count again (Schwellwert)

// Stretches the part of the target the camera drew over the canvas (bilinear)
class UpscalePass extends RenderPassShaderQuad {
    constructor(device, target) {
        super(device);
        this.target = target;
        this.shader = ShaderUtils.createShader(device, {
            uniqueName: 'GovernorUpscale',
            attributes: { aPosition: SEMANTIC_POSITION },
            vertexChunk: 'quadVS',
            fragmentGLSL: `
                uniform sampler2D governorSource;
                uniform vec4 governorUv;
                uniform vec4 governorClamp;
                varying vec2 uv0;
                void main(void) {
                    gl_FragColor = texture2D(governorSource, clamp(uv0 * governorUv.xy + governorUv.zw, governorClamp.xy, governorClamp.zw));
                }`,
            fragmentWGSL: `
                var governorSource: texture_2d<f32>;
                var governorSourceSampler: sampler;
                uniform governorUv: vec4f;
                uniform governorClamp: vec4f;
                varying uv0: vec2f;
                @fragment fn fragmentMain(input: FragmentInput) -> FragmentOutput {
                    var output: FragmentOutput;
                    let uv = clamp(input.uv0 * uniform.governorUv.xy + uniform.governorUv.zw, uniform.governorClamp.xy, uniform.governorClamp.zw);
                    output.color = textureSample(governorSource, governorSourceSampler, uv);
                    return output;
                }`
        });
        this.uv = new Float32Array([1, 1, 0, 0]);        // scale xy, offset zw
        this.uvClamp = new Float32Array([0, 0, 1, 1]);   // outermost texel centres of the drawn part: nothing stale bleeds in
        this.sourceId = device.scope.resolve('governorSource');
        this.uvId = device.scope.resolve('governorUv');
        this.clampId = device.scope.resolve('governorClamp');
        this.init(null);
        this.setClearColor(Color.BLACK);   // every pixel is drawn; a clear spares tiled GPUs loading the old frame
    }

    /** The camera drew w × h pixels at the viewport origin (lower left; WebGPU keeps textures top down). */
    setPart(w, h) {
        const W = this.target.width, H = this.target.height, down = this.device.isWebGPU;
        this.uv.set([w / W, h / H, 0, down ? 1 - h / H : 0]);
        this.uvClamp.set([0.5 / W, down ? 1 - (h - 0.5) / H : 0.5 / H, (w - 0.5) / W, down ? 1 - 0.5 / H : (h - 0.5) / H]);
    }

    execute() {
        this.sourceId.setValue(this.target.colorBuffer);
        this.uvId.setValue(this.uv);
        this.clampId.setValue(this.uvClamp);
        super.execute();
    }
}

export class FrameGovernor {
    /**
     * @param {import('playcanvas').AppBase} app
     * @param {import('playcanvas').Entity} camera
     * @param {{cap:number, start:number, min?:number}} ratio - pixel ratio limits
     * @param {{scale?:boolean}} [opts] - scale: render scale in a fixed target instead of canvas resizes (see top)
     */
    constructor(app, camera, ratio, opts = {}) {
        this.app = app;
        this.camera = camera;
        this.device = app.graphicsDevice;
        this.cap = ratio.cap;
        this.min = ratio.min ?? Math.min(0.75, ratio.cap);
        this.ratio = Math.min(ratio.start, this.cap);
        this.blockedAbove = Infinity;   // ratio that caused drops recently
        this.blockedUntil = 0;
        this.window = [];               // frame intervals while rendering continuously
        this.windowStart = performance.now();
        this.goodSeconds = 0;
        this.busyUntil = 0;
        this.loading = true;
        this.lastPose = null;
        this.lastMove = performance.now();
        this.pendingRaise = null;       // higher ratio earned while moving, applied once the camera rests
        this.lastRender = null;
        this.holds = new Set();         // reasons that force continuous rendering (intro, loading)
        this.restRatio = 0;             // optional: sharper ratio for the frames drawn while the camera rests
        this.resting = false;
        this.locked = false;            // lock(): the ratio stays as it is from now on
        this.onStarved = null;          // frames keep dropping at the lowest ratio: the caller may draw less
        this.scaled = !!opts.scale;

        if (this.scaled) this._initScale(); else this._applyRatio();
        app.autoRender = false;
        app.renderNextFrame = true;

        // Streaming state comes from the gsplat system; keep drawing while chunks arrive
        app.systems.gsplat?.on('frame:ready', (cam, layer, ready, loadingCount) => {
            if (!ready || loadingCount > 0) this.wake();
        });
        app.on('update', () => this._tick());
        app.on('framerender', () => this._measure());
        window.addEventListener('resize', () => this.wake());
    }

    /** Something changed the picture: render for at least the grace period. */
    wake(seconds = STILL_GRACE) {
        this.busyUntil = Math.max(this.busyUntil, performance.now() + seconds * 1000);
        this.app.renderNextFrame = true;
    }

    /**
     * Keep the pixel ratio as it is from now on. Every change resizes the canvas, a visible hitch with a jump in
     * sharpness; a page that leads the camera (the website) settles the ratio while nobody moves and then locks it.
     * In scale mode the GPU budget's share stays as it is.
     */
    lock() {
        this.locked = true;
        this.pendingRaise = null;
    }

    /**
     * New pixel ratio limits (the picture quality changed): start over from `start` (at most `below`) and forget
     * earlier drops.
     */
    setLimits(ratio, below = Infinity) {
        this.cap = ratio.cap;
        this.min = ratio.min ?? Math.min(0.75, ratio.cap);
        this.ratio = Math.max(this.min, Math.min(ratio.start, this.cap, below));
        this.blockedAbove = Infinity;
        this.blockedUntil = 0;
        this.pendingRaise = null;
        this.goodSeconds = this.badSeconds = 0;
        this.resting = false;
        if (this.scaled) {
            // Another cap resizes the canvas once: a change of picture quality, not a frame-rate step
            this._scaleLimits();
            if (this.device.maxPixelRatio !== this.cap) { this.device.maxPixelRatio = this.cap; this.app.resizeCanvas(); }
            this.wake();
            return;
        }
        this._applyRatio();
    }

    hold(reason, on) {
        if (on) this.holds.add(reason); else this.holds.delete(reason);
        this.wake();
    }

    _pose() {
        const p = this.camera.getPosition(), r = this.camera.getRotation();
        return [p.x, p.y, p.z, r.x, r.y, r.z, r.w, this.camera.camera.fov];
    }

    _tick() {
        const pose = this._pose();
        const moved = !this.lastPose || pose.some((v, i) => Math.abs(v - this.lastPose[i]) > 1e-6);
        this.lastPose = pose;
        if (this.scaled) this._turnSpeed();
        if (moved) {
            this.lastMove = performance.now();
            // Back to the moving ratio. Callers that use the rest ratio start moves from zero speed, so the
            // resize stall lands on a frame where the picture hardly changes
            if (this.resting) { this.resting = false; if (!this.scaled) this._applyRatio(); }
            this.wake();
        } else if (this.scaled) {
            // The picture has settled (the grace period is over): one last frame at full resolution, then none until
            // the next change. Moving frames start from the budget's scale again.
            if (this.holds.size || performance.now() < this.busyUntil) this.resting = false;
            else if (!this.resting && this.targetOn && this.sx * this.sy < 1) { this.resting = true; this.app.renderNextFrame = true; }
        } else if (this.restRatio > this.ratio && !this.resting && !this.holds.size && performance.now() - this.lastMove > STILL_FOR_RAISE * 1000) {
            // Standing still costs one frame, not a frame rate: draw it at full sharpness
            this.resting = true;
            this.pendingRaise = null;
            this.device.maxPixelRatio = this.restRatio;
            this.app.resizeCanvas();
            this.wake();
        } else if (this.pendingRaise !== null && performance.now() - this.lastMove > STILL_FOR_RAISE * 1000) {
            // A canvas resize stalls a frame; while standing still nobody sees it
            this.ratio = this.pendingRaise;
            this.pendingRaise = null;
            this._applyRatio();
        }
        if (this.holds.size || performance.now() < this.busyUntil) this.app.renderNextFrame = true;
    }

    _measure() {
        // 'framerender' fires every tick; only ticks that actually render count. Frames at the rest ratio
        // say nothing about the moving frame rate, frames in VR (the headset sets the resolution) nothing about the screen.
        if (!this.app.renderNextFrame || this.resting || this.app.xr?.active) {
            this.lastRender = null;
            // A miss from before the pause does not belong to the next run of frames
            if (this.gaps) this.gaps.length = 0;
            return;
        }
        const now = performance.now();
        // Only consecutive rendered frames say something about GPU load; gaps are idle time
        if (this.lastRender !== null) {
            this.window.push(now - this.lastRender);
            if (this.scaled && !this.timed) this._frameGap(now - this.lastRender);
        }
        this.lastRender = now;
        if (now - this.windowStart < 1000) return;
        // A slow device draws few frames a second; it has to be measured all the more: over up to three seconds
        if (this.window.length < 8 && now - this.windowStart < 3000) return;
        const frames = this.window;
        this.window = [];
        this.windowStart = now;
        if (frames.length < 4) return;
        frames.sort((a, b) => a - b);
        const median = frames[frames.length >> 1];
        const p90 = frames[Math.floor(frames.length * 0.9)];
        this.fps = Math.round(1000 / median);
        // Lower only on sustained drops (two seconds below ~45 fps); single hitches come from
        // scheduling, streaming or sorting and are not cured by a blurrier picture
        this.badSeconds = median > 22 ? (this.badSeconds || 0) + 1 : 0;
        this.p90 = Math.round(p90);
        if (this.scaled) {
            // The render scale follows every frame (_gpuTime, _frameGap); starved means frames keep dropping at its floor
            if (this.badSeconds >= 3) {
                this.badSeconds = 0;
                if (!this.locked && this.area <= this.minArea * 1.01) this.onStarved?.();
            }
            return;
        }
        if (this.locked) return;
        if (this.badSeconds >= 3 && this.ratio > this.min) {
            this.badSeconds = 0;
            // Dropping frames: lower resolution and never go back up this session. Retrying later made
            // the canvas resize back and forth, and every resize is a visible hitch.
            this.blockedAbove = this.ratio;
            this.blockedUntil = Infinity;
            this.ratio = Math.max(this.min, this.ratio - STEP);
            this.pendingRaise = null;
            this.goodSeconds = 0;
            this._applyRatio();
        } else if (this.badSeconds >= 3) {
            // Already at the lowest ratio
            this.badSeconds = 0;
            this.onStarved?.();
        } else if (median < 17.5 && p90 < 20) {
            this.goodSeconds++;
            const next = Math.min(this.cap, this.ratio + STEP);
            const blocked = now < this.blockedUntil && next >= this.blockedAbove;
            if (this.goodSeconds >= 4 && next > this.ratio && !blocked && this.pendingRaise === null) {
                // Raising while walking was a visible hitch in the middle of the movement: wait for a pause
                this.pendingRaise = next;
                this.goodSeconds = 0;
            }
        } else {
            this.goodSeconds = 0;
        }
    }

    _applyRatio() {
        this.device.maxPixelRatio = this.ratio;
        this.app.resizeCanvas();
        this.wake();
    }

    // --- scale mode ---

    _initScale() {
        const { app, device } = this;
        this.sx = this.sy = 1;          // scale per axis of the last frame drawn (part of the target in use)
        this.force = null;              // tests: a fixed {x, y} scale instead of the control
        this.turn = 0;                  // yaw speed, deg/s, smoothed
        this.lastYaw = null;
        this.lastTick = performance.now();
        this.load = 0;                  // GPU time / GPU_TARGET at the budget's share, smoothed (0..1 = within budget)
        this.headroom = [];             // recent loads within budget: raise when nearly all of them leave room
        this.recent = [];               // last GPU times (ms, at the budget's share): cut on their median
        this.raiseAfter = 0;
        this.drawn = new Map();         // render version -> pixel share drawn (GPU timings arrive a few frames late)
        this.gaps = [];                 // WebGL2: last frames, true = missed vsync while the main thread was not busy
        this.ceiling = 1;
        this.ceilingUntil = 0;
        this.cutQuiet = 0;
        this.prevArea = 1;
        this.tickStart = this.tickMs = 0;
        this.streamUntil = 0;           // WebGL2: chunks stream until then (their decoding runs between frames)
        this.version = device.renderVersion;
        this.minPixel = app.scene.gsplat.minPixelSize;
        this.rect = new Vec4(0, 0, 1, 1);
        this.partW = this.partH = -1;
        this._scaleLimits();
        device.maxPixelRatio = this.cap;
        app.resizeCanvas();

        // The scene goes into a target of canvas size; a camera after the scene's draws the drawn part on the canvas
        const colorBuffer = new Texture(device, {
            name: 'governor-scene', width: device.width, height: device.height, format: PIXELFORMAT_RGBA8, mipmaps: false,
            minFilter: FILTER_LINEAR, magFilter: FILTER_LINEAR, addressU: ADDRESS_CLAMP_TO_EDGE, addressV: ADDRESS_CLAMP_TO_EDGE
        });
        this.target = new RenderTarget({ name: 'governor-scene', colorBuffer, depth: true });
        this.upscale = new UpscalePass(device, this.target);
        this.upscaleCamera = new Entity('governor-upscale');
        this.upscaleCamera.addComponent('camera', { priority: 1000, layers: [LAYERID_UI], clearColorBuffer: false, clearDepthBuffer: false });
        this.upscaleCamera.camera.framePasses = [this.upscale];
        app.root.addChild(this.upscaleCamera);
        this._useTarget(true);

        // GPU time per frame: WebGPU timestamp queries, reported with the frame's render version. Without them
        // (WebGL2) missed vsyncs that the main thread does not explain stand in (_frameGap).
        // A lost and restored WebGPU device brings a new profiler: hook that one too, or the scale stops following
        const hook = () => {
            const prof = device.gpuProfiler;
            this.timed = !!(device.isWebGPU && device.supportsTimestampQuery && prof);
            if (!this.timed || prof.__governor) return;
            prof.__governor = true;
            prof.enabled = true;
            const report = prof.report.bind(prof);
            prof.report = (version, timings, frameTime) => {
                report(version, timings, frameTime);
                if (timings?.length) this._gpuTime(version, frameTime ?? timings.reduce((s, t) => s + t, 0));
            };
        };
        hook();
        device.on('devicerestored', () => {
            hook();
            this.drawn.clear();
            this.recent.length = this.headroom.length = this.gaps.length = 0;
        });
        // Only loads decode between frames; a world rebuild without them runs inside the tick and counts as busy
        app.systems.gsplat?.on('frame:ready', (cam, layer, ready, loadingCount) => {
            if (loadingCount > 0) this.streamUntil = performance.now() + STREAM_TAIL;
        });
        app.on('frameupdate', () => { this.tickStart = performance.now(); });
        app.on('framerender', () => this._frame());
        app.on('frameend', () => {
            this.tickMs = performance.now() - this.tickStart;
            if (device.renderVersion === this.version) return;
            this.version = device.renderVersion;
            this.prevArea = this.sx * this.sy;
            if (this.timed && this.targetOn && !this.resting && !this.force) {
                this.drawn.set(this.version, this.prevArea);
                if (this.drawn.size > 64) this.drawn.delete(this.drawn.keys().next().value);
            }
        });
    }

    _scaleLimits() {
        // Never below today's lowest pixel ratio; a fixed ?ratio= (min = cap) keeps the scale at 1
        this.floor = Math.max(SCALE_FLOOR, Math.min(1, this.min / this.cap));
        this.minArea = this.floor * this.floor;
        this.ceiling = 1;
        this.headroom.length = this.recent.length = 0;
        this._setArea((this.ratio / this.cap) ** 2);
    }

    /** Pixel share (sx·sy) the GPU budget allows; `ratio` reports it as a pixel ratio */
    _setArea(a) {
        this.area = Math.max(this.minArea, Math.min(1, a));
        this.ratio = Math.round(this.cap * Math.sqrt(this.area) * 100) / 100;
    }

    _useTarget(on) {
        const cam = this.camera.camera;
        this.targetOn = on;
        cam.renderTarget = on ? this.target : null;
        // The camera's part of the target must keep the canvas's aspect, not the part's
        cam.aspectRatioMode = on ? ASPECT_MANUAL : ASPECT_AUTO;
        this.upscaleCamera.enabled = on;
        if (!on) {
            cam.rect = this.rect.set(0, 0, 1, 1);
            this.app.scene.gsplat.minPixelSize = this.minPixel;
            this.sx = this.sy = 1;
        }
        this.partW = this.partH = -1;
    }

    _turnSpeed() {
        const now = performance.now(), dt = (now - this.lastTick) / 1000;
        const f = this.camera.forward, yaw = Math.atan2(f.x, f.z) * 180 / Math.PI;
        if (this.lastYaw !== null && dt > 0) {
            const speed = Math.abs((((yaw - this.lastYaw) % 360) + 540) % 360 - 180) / Math.max(dt, 1 / 240);
            this.turn += (speed - this.turn) * (1 - Math.exp(-dt / 0.08));
        }
        this.lastYaw = yaw;
        this.lastTick = now;
    }

    /** Before a frame is drawn: the part of the target it uses */
    _frame() {
        if (!this.app.renderNextFrame) return;
        // VR draws as before: the headset sets the resolution
        if (this.app.xr?.active) {
            if (this.targetOn) this._useTarget(false);
            return;
        }
        if (!this.targetOn) this._useTarget(true);
        // The canvas size render() would set: right after VR the device still has the headset's size
        this.app.updateCanvasSize();
        const d = this.device, cam = this.camera.camera;
        if (!d.width || !d.height) return;
        if (this.target.width !== d.width || this.target.height !== d.height) {
            this.target.resize(d.width, d.height);
            this.partW = this.partH = -1;
        }
        // Something woke the picture after its rest frame was decided (a handler after _tick): draw at the budget's scale
        if (this.resting && (this.holds.size || performance.now() < this.busyUntil)) this.resting = false;
        const [x, y] = this.force ? [this.force.x, this.force.y] : this.resting ? [1, 1] : this._split();
        const w = Math.max(1, Math.round(d.width * x)), h = Math.max(1, Math.round(d.height * y));
        if (w !== this.partW || h !== this.partH) {
            this.partW = w;
            this.partH = h;
            // (a hair over the pixel edge, so the engine's floor() lands on w × h)
            cam.rect = this.rect.set(0, 0, (w + 1e-3) / d.width, (h + 1e-3) / d.height);
            this.upscale.setPart(w, h);
            // The engine culls splats under minPixelSize render pixels: count native pixels, so a smaller part
            // drops no more fine detail than a whole one
            this.app.scene.gsplat.minPixelSize = this.minPixel * Math.min(w / d.width, h / d.height);
        }
        cam.aspectRatio = d.width / d.height;
        this.sx = w / d.width;
        this.sy = h / d.height;
    }

    /** Scale per axis for the next frame: the budget's pixel share, taken from x first while turning */
    _split() {
        const F = this.area, floor = this.floor;
        // A turn smears the display sideways (sample-and-hold), so x detail is not seen: x may drop with the yaw
        // speed, also ahead of the budget once the GPU is busy (a turn sweeps into heavier views faster than the
        // budget follows). Up and down stay sharp.
        const busy = F < 1 || this.load > 0.6;
        const t = busy ? Math.min(1, Math.max(0, (this.turn - TURN[0]) / (TURN[1] - TURN[0]))) : 0;
        const xt = 1 - t * (1 - floor);
        let y = Math.min(1, Math.sqrt(F / xt));
        const x = Math.max(floor, Math.min(xt, F / y));
        y = Math.max(floor, Math.min(1, F / x));
        const snap = v => Math.min(1, Math.max(floor, Math.round(v * SCALE_SNAP) / SCALE_SNAP));
        return [snap(x), snap(y)];
    }

    /** WebGPU: the GPU time of frame `version` arrived */
    _gpuTime(version, ms) {
        const drawn = this.drawn.get(version);
        for (const k of this.drawn.keys()) { if (k > version) break; this.drawn.delete(k); }
        if (drawn === undefined || !(ms > 0)) return;
        // GPU time as if drawn at the budget's share (a turn may have drawn less); only FILL_SHARE scales with pixels
        const fixed = 1 - FILL_SHARE, t = ms * (fixed + FILL_SHARE * this.area / drawn);
        this.load += (t / GPU_TARGET - this.load) * 0.1;
        this.recent.push(t);
        if (this.recent.length > 3) this.recent.shift();
        if (this.locked) return;
        const now = performance.now();
        const mid = this.recent.length === 3 ? [...this.recent].sort((a, b) => a - b)[1] : 0;
        if (mid > GPU_TARGET) {
            // Over budget for more than one frame (a single slow one is streaming or the page, not fill): cut at once
            // to the share the model puts on target
            this._setArea(this.area * Math.max(0, (GPU_TARGET / mid - fixed) / FILL_SHARE));
            this.recent.length = this.headroom.length = 0;
            this.raiseAfter = now + 1000;
        } else if (this.area < 1) {
            this.headroom.push(t);
            if (this.headroom.length >= 20 && now > this.raiseAfter) {
                // Raise only as far as the second heaviest recent frame stays within 85 % of the target
                const worst = this.headroom.sort((a, b) => b - a)[1];
                const k = (0.85 * GPU_TARGET / worst - fixed) / FILL_SHARE;
                if (k > 1.02) this._setArea(this.area * Math.min(1.25, k));
                this.headroom.length = 0;
                this.raiseAfter = now + 500;
            }
        }
    }

    /** No GPU timer (WebGL2): the interval `dt` before this frame */
    _frameGap(dt) {
        const now = performance.now();
        // While chunks stream, their decoding runs as tasks between frames, outside any tick: a late frame then
        // says nothing about the GPU, and an early one nothing about headroom
        if (now < this.streamUntil) return;
        // A missed vsync after short ticks: the GPU fell behind, not the page's code (sorting, readbacks, decoding).
        // The interval holds the last tick and this tick's update so far.
        const busy = this.tickMs + (now - this.tickStart);
        const miss = dt > MISSED && busy < MISSED / 2;
        this.gaps.push(miss);
        if (this.gaps.length > 8) this.gaps.shift();
        this.load = miss ? 1 : this.load * 0.998;
        if (miss) this.raiseAfter = Math.max(this.raiseAfter, now + 2000);
        if (this.locked) return;
        if (miss && now > this.cutQuiet && this.gaps.filter(Boolean).length >= 2) {
            // Two misses within a few frames (one alone may be a task between frames): cut, and do not probe that
            // share again for a while
            this.ceiling = this.prevArea;
            this.ceilingUntil = now + 10000;
            this._setArea(Math.min(this.area, this.prevArea * 0.75));
            this.cutQuiet = now + 300;
            this.gaps.length = 0;
        } else if (!miss && now > this.raiseAfter && this.area < 1) {
            const top = now < this.ceilingUntil ? this.ceiling * 0.9 : 1;
            if (this.area < top) this._setArea(Math.min(top, this.area * 1.08));
            this.raiseAfter = now + 500;
        }
    }
}
